import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { DataRequest } from "../../models/data-request.model";

import { createDataRequestMutation } from "./data-request.gql";

interface CreateDataRequestResponse {
  createDataRequest: DataRequest;
}

export interface CreateDataRequestInput {
  title: string;
  dueDate: string;
  workspaceId: string;
  entities: Array<{
    entityId: string;
    entityType: string;
    assigneeId?: string;
  }>;
  fieldSelection?: string[];
  enableDocuments?: boolean;
  freeTextQuestions?: string[];
}

export interface UseCreateDataRequestProps {
  onCompleted?: (data: DataRequest) => void;
  onError?: (error: unknown) => void;
}

export const useCreateDataRequestMutation = ({ onCompleted, onError }: UseCreateDataRequestProps = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: CreateDataRequestInput) => {
      const client = createOperationClient("CreateDataRequest");
      const data = await client.request<CreateDataRequestResponse>(createDataRequestMutation, {
        input,
      });
      return data.createDataRequest;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dataRequests.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() });
      if (onCompleted) onCompleted(data);
    },
    onError: (error) => {
      if (onError) onError(error);
    },
  });

  return {
    createDataRequest: mutation.mutate,
    createDataRequestAsync: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
